import React, { useCallback, useEffect, useState } from 'react';
import { useAdminApiService } from '../../hooks/useAdminApiService';
import './AdminComponents.css';

export interface PlatformCoinRow {
  id: number;
  name: string;
  symbol: string;
  blockchain: string;
  networkType: string;
  contractAddress?: string | null;
  deploymentTxHash?: string | null;
  tokenStandard: string;
  decimals: number;
  totalSupply?: number | string | null;
  status: string;
  notes?: string | null;
  kucoinBaseSymbol?: string | null;
  kucoinSpotPair?: string | null;
  kucoinFuturesSymbol?: string | null;
  tradingEnabled?: boolean;
  createdAt?: string;
  updatedAt?: string;
}

export interface AirdropRecipientRow {
  id: number;
  userId: number;
  username: string;
  walletAddress?: string | null;
  blockchain?: string | null;
  amount: number | string;
  status: string;
  validationMessage?: string | null;
  txHash?: string | null;
  errorMessage?: string | null;
}

export interface AirdropCampaignRow {
  id: number;
  platformCoinId: number;
  platformCoinSymbol?: string;
  platformCoinBlockchain?: string;
  name: string;
  amountPerUser: number | string;
  eligibility: string;
  deliveryMode?: string;
  status: string;
  eligibleCount?: number;
  missingWalletCount?: number;
  invalidAddressCount?: number;
  dryRunOkCount?: number;
  sentCount?: number;
  failedCount?: number;
  claimableCount?: number;
  recipients?: AirdropRecipientRow[];
  createdAt?: string;
}

const emptyCoinForm = {
  name: '',
  symbol: '',
  blockchain: 'ethereum',
  networkType: 'TESTNET',
  contractAddress: '',
  tokenStandard: 'ERC-20',
  decimals: 18,
  totalSupply: '',
  notes: '',
};

const emptyCampaignForm = {
  platformCoinId: '',
  name: '',
  amountPerUser: '',
  eligibility: 'ALL_WITH_WALLET',
  deliveryMode: 'PUSH',
};

const AdminPlatformCoins: React.FC = () => {
  const adminApi = useAdminApiService();
  const [coins, setCoins] = useState<PlatformCoinRow[]>([]);
  const [campaigns, setCampaigns] = useState<AirdropCampaignRow[]>([]);
  const [selectedCampaign, setSelectedCampaign] = useState<AirdropCampaignRow | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [coinForm, setCoinForm] = useState(emptyCoinForm);
  const [campaignForm, setCampaignForm] = useState(emptyCampaignForm);
  const [savingCoin, setSavingCoin] = useState(false);
  const [savingCampaign, setSavingCampaign] = useState(false);
  const [actionBusy, setActionBusy] = useState(false);
  const [listingStatusId, setListingStatusId] = useState<number | null>(null);
  const [listingStatus, setListingStatus] = useState('LISTING_PENDING');
  const [adminWallets, setAdminWallets] = useState<Array<{ id: number; blockchain?: string; address?: string }>>([]);
  const [fundingWalletId, setFundingWalletId] = useState('');
  const [executeConfig, setExecuteConfig] = useState<{
    executeEnabled?: boolean;
    blockchainEnabled?: boolean;
    allowSimulatedExecute?: boolean;
  } | null>(null);
  const [executeCampaignId, setExecuteCampaignId] = useState<number | null>(null);
  const [deployConfig, setDeployConfig] = useState<{ deployEnabled?: boolean; blockchainEnabled?: boolean } | null>(null);
  const [deployCoinId, setDeployCoinId] = useState<number | null>(null);
  const [deployWalletId, setDeployWalletId] = useState('');
  const [kucoinBase, setKucoinBase] = useState('');
  const [kucoinSpot, setKucoinSpot] = useState('');
  const [tradingEnabled, setTradingEnabled] = useState(false);

  const loadAll = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const [coinsRes, campaignsRes, walletsRes, cfgRes, deployCfgRes] = await Promise.all([
        adminApi.getPlatformCoins(),
        adminApi.getAirdropCampaigns(),
        adminApi.listAdminCryptoWallets(),
        adminApi.getAirdropExecuteConfig(),
        adminApi.getPlatformCoinDeployConfig(),
      ]);
      if (coinsRes.success && coinsRes.data) {
        setCoins(Array.isArray(coinsRes.data) ? coinsRes.data : []);
      } else {
        setError(coinsRes.error || 'Failed to load platform coins');
      }
      if (campaignsRes.success && campaignsRes.data) {
        setCampaigns(Array.isArray(campaignsRes.data) ? campaignsRes.data : []);
      } else if (coinsRes.success) {
        setError(campaignsRes.error || 'Failed to load airdrop campaigns');
      }
      if (walletsRes.success && walletsRes.data) {
        setAdminWallets(Array.isArray(walletsRes.data) ? walletsRes.data : []);
      }
      if (cfgRes.success && cfgRes.data) {
        setExecuteConfig(cfgRes.data);
      }
      if (deployCfgRes.success && deployCfgRes.data) {
        setDeployConfig(deployCfgRes.data);
      }
    } catch (err: any) {
      setError(err.message || 'Failed to load data');
    } finally {
      setLoading(false);
    }
  }, [adminApi]);

  useEffect(() => {
    loadAll();
  }, [loadAll]);

  const handleCreateCoin = async () => {
    if (!coinForm.name.trim() || !coinForm.symbol.trim()) {
      setError('Name and symbol are required');
      return;
    }
    setSavingCoin(true);
    setError(null);
    setSuccess(null);
    try {
      const payload: any = {
        name: coinForm.name.trim(),
        symbol: coinForm.symbol.trim(),
        blockchain: coinForm.blockchain,
        networkType: coinForm.networkType,
        contractAddress: coinForm.contractAddress.trim() || null,
        tokenStandard: coinForm.tokenStandard,
        decimals: Number(coinForm.decimals) || 18,
        notes: coinForm.notes.trim() || null,
      };
      if (coinForm.totalSupply.trim()) {
        payload.totalSupply = coinForm.totalSupply.trim();
      }
      const res = await adminApi.createPlatformCoin(payload);
      if (res.success) {
        setCoinForm(emptyCoinForm);
        setSuccess('Platform coin registered (no on-chain deploy — paste contract after you deploy externally)');
        await loadAll();
      } else {
        setError(res.error || 'Failed to register coin');
      }
    } catch (err: any) {
      setError(err.message || 'Failed to register coin');
    } finally {
      setSavingCoin(false);
    }
  };

  const handleUpdateListingStatus = async () => {
    if (!listingStatusId) {
      setError('Select a coin to update listing status');
      return;
    }
    setActionBusy(true);
    setError(null);
    setSuccess(null);
    try {
      const payload: any = {
        status: listingStatus,
        kucoinBaseSymbol: kucoinBase.trim() || undefined,
        kucoinSpotPair: kucoinSpot.trim() || undefined,
        tradingEnabled,
      };
      const res = await adminApi.updatePlatformCoin(listingStatusId, payload);
      if (res.success) {
        setSuccess(
          `Coin updated (status ${listingStatus}` +
            (tradingEnabled ? ', trading enabled in strategy universes' : '') +
            ')'
        );
        await loadAll();
      } else {
        setError(res.error || 'Failed to update status');
      }
    } catch (err: any) {
      setError(err.message || 'Failed to update status');
    } finally {
      setActionBusy(false);
    }
  };

  const runTestnetDeploy = async () => {
    if (!deployCoinId) {
      setError('Select a TESTNET coin to deploy');
      return;
    }
    if (!deployWalletId) {
      setError('Select a deployer funding wallet');
      return;
    }
    if (!window.confirm('Deploy SimplePlatformERC20 on TESTNET? Gas will be paid by the funding wallet; full supply is minted to that wallet.')) {
      return;
    }
    setActionBusy(true);
    setError(null);
    setSuccess(null);
    try {
      const res = await adminApi.deployPlatformCoinTestnet(deployCoinId, {
        fundingWalletId: Number(deployWalletId),
        confirmDeploy: true,
      });
      if (res.success) {
        setSuccess(`Deployed at ${res.data?.contractAddress || '—'} (tx ${res.data?.deploymentTxHash || '—'})`);
        await loadAll();
      } else {
        setError(res.error || 'Deploy failed');
      }
    } catch (err: any) {
      setError(err.message || 'Deploy failed');
    } finally {
      setActionBusy(false);
    }
  };

  const handleCreateCampaign = async () => {
    if (!campaignForm.platformCoinId || !campaignForm.name.trim() || !campaignForm.amountPerUser) {
      setError('Coin, campaign name, and amount per user are required');
      return;
    }
    setSavingCampaign(true);
    setError(null);
    setSuccess(null);
    try {
      const res = await adminApi.createAirdropCampaign({
        platformCoinId: Number(campaignForm.platformCoinId),
        name: campaignForm.name.trim(),
        amountPerUser: campaignForm.amountPerUser,
        eligibility: campaignForm.eligibility,
        deliveryMode: campaignForm.deliveryMode,
      });
      if (res.success) {
        setCampaignForm(emptyCampaignForm);
        setSuccess('Airdrop campaign created as DRAFT');
        await loadAll();
      } else {
        setError(res.error || 'Failed to create campaign');
      }
    } catch (err: any) {
      setError(err.message || 'Failed to create campaign');
    } finally {
      setSavingCampaign(false);
    }
  };

  const openCampaignDetail = async (id: number) => {
    setActionBusy(true);
    setError(null);
    try {
      const res = await adminApi.getAirdropCampaign(id, true);
      if (res.success && res.data) {
        setSelectedCampaign(res.data);
      } else {
        setError(res.error || 'Failed to load campaign');
      }
    } catch (err: any) {
      setError(err.message || 'Failed to load campaign');
    } finally {
      setActionBusy(false);
    }
  };

  const runPreview = async (id: number) => {
    setActionBusy(true);
    setError(null);
    setSuccess(null);
    try {
      const res = await adminApi.previewAirdropCampaign(id);
      if (res.success && res.data) {
        setSelectedCampaign(res.data);
        setSuccess('Recipient preview built — no tokens sent');
        await loadAll();
      } else {
        setError(res.error || 'Preview failed');
      }
    } catch (err: any) {
      setError(err.message || 'Preview failed');
    } finally {
      setActionBusy(false);
    }
  };

  const runDryRun = async (id: number) => {
    setActionBusy(true);
    setError(null);
    setSuccess(null);
    try {
      const res = await adminApi.dryRunAirdropCampaign(id);
      if (res.success && res.data) {
        setSelectedCampaign(res.data);
        setSuccess('Dry-run completed — no on-chain transfers');
        await loadAll();
      } else {
        setError(res.error || 'Dry-run failed');
      }
    } catch (err: any) {
      setError(err.message || 'Dry-run failed');
    } finally {
      setActionBusy(false);
    }
  };

  const cancelCampaign = async (id: number) => {
    if (!window.confirm('Cancel this airdrop campaign?')) return;
    setActionBusy(true);
    setError(null);
    setSuccess(null);
    try {
      const res = await adminApi.cancelAirdropCampaign(id);
      if (res.success) {
        setSuccess('Campaign cancelled');
        setSelectedCampaign(null);
        await loadAll();
      } else {
        setError(res.error || 'Cancel failed');
      }
    } catch (err: any) {
      setError(err.message || 'Cancel failed');
    } finally {
      setActionBusy(false);
    }
  };

  const runExecute = async () => {
    if (!executeCampaignId) {
      setError('Select a campaign to execute');
      return;
    }
    if (!fundingWalletId) {
      setError('Select a funding wallet (create one under Crypto Payouts if needed)');
      return;
    }
    const campaign = campaigns.find((c) => c.id === executeCampaignId);
    const mode = campaign?.deliveryMode || 'PUSH';
    const confirmMsg =
      mode === 'CLAIM'
        ? 'Open claims for this campaign? Tokens will leave the funding wallet when users claim.'
        : 'EXECUTE LIVE PUSH AIRDROP?\n\nThis sends ERC-20 tokens from the selected funding wallet to all dry-run-OK recipients.\nThis cannot be undone.';
    if (!window.confirm(confirmMsg)) return;
    if (mode === 'PUSH' && !window.confirm('Type confirmation: really send on-chain transfers now?')) return;

    setActionBusy(true);
    setError(null);
    setSuccess(null);
    try {
      const res = await adminApi.executeAirdropCampaign(executeCampaignId, {
        fundingWalletId: Number(fundingWalletId),
        confirmLiveSend: true,
      });
      if (res.success) {
        const data = res.data || {};
        setSuccess(
          mode === 'CLAIM'
            ? `Claims opened (${data.claimableCount ?? 0} claimable)`
            : `Execute done — sent ${data.successCount ?? 0}, failed ${data.failureCount ?? 0}${data.simulated ? ' (SIMULATED)' : ''}`
        );
        await loadAll();
        await openCampaignDetail(executeCampaignId);
      } else {
        setError(res.error || 'Execute failed');
      }
    } catch (err: any) {
      setError(err.message || 'Execute failed');
    } finally {
      setActionBusy(false);
    }
  };

  const statusBadge = (status: string) => {
    const color =
      status === 'REGISTERED' || status === 'DRY_RUN_OK' || status === 'ELIGIBLE'
        ? '#0dca9d'
        : status === 'MISSING_WALLET' || status === 'INVALID_ADDRESS' || status === 'CANCELLED'
          ? '#ff7171'
          : status === 'LISTING_PENDING' || status === 'MAINNET'
            ? '#fbb445'
            : '#086afb';
    return (
      <span
        style={{
          display: 'inline-block',
          padding: '2px 8px',
          borderRadius: 999,
          fontSize: 11,
          fontWeight: 600,
          background: `${color}22`,
          color,
        }}
      >
        {status}
      </span>
    );
  };

  return (
    <div className="admin-component">
      <div className="section-header">
        <h2 className="section-title">Platform Coins &amp; Airdrops</h2>
        <button className="refresh-btn" onClick={loadAll} disabled={loading}>
          {loading ? 'Refreshing...' : 'Refresh'}
        </button>
      </div>

      <div
        style={{
          padding: '12px 14px',
          marginBottom: 16,
          borderRadius: 8,
          border: '1px solid #fbb445',
          background: '#fff8eb',
          color: '#7a4e00',
          fontSize: 13,
          maxWidth: 900,
        }}
      >
        <strong>Phase 2 (gated):</strong> Preview / Dry-run remain safe. Live send requires{' '}
        <code>platform.airdrop.execute-enabled=true</code>
        {executeConfig?.blockchainEnabled ? '' : ' and preferably '}
        {!executeConfig?.blockchainEnabled && <code>blockchain.enabled=true</code>}
        . Execute is currently{' '}
        <strong>{executeConfig?.executeEnabled ? 'ENABLED' : 'DISABLED'}</strong>
        {executeConfig?.allowSimulatedExecute ? ' (simulated sends allowed)' : ''}.
        PUSH sends from your admin funding wallet; CLAIM arms recipients so users claim in the retail app.
      </div>

      {error && (
        <div style={{ marginBottom: 12, padding: 10, borderRadius: 8, background: '#ff717122', color: '#b42318', fontSize: 13 }}>
          {error}
        </div>
      )}
      {success && (
        <div style={{ marginBottom: 12, padding: 10, borderRadius: 8, background: '#0dca9d22', color: '#0a7a5e', fontSize: 13 }}>
          {success}
        </div>
      )}

      {/* Register coin */}
      <div style={{ background: '#fff', border: '1px solid #e5e7eb', borderRadius: 10, padding: 16, marginBottom: 20 }}>
        <h3 style={{ marginTop: 0, fontSize: 16 }}>Register platform coin</h3>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(180px, 1fr))', gap: 10 }}>
          <label style={{ fontSize: 12, display: '#6b7280' }}>
            Name
            <input
              value={coinForm.name}
              onChange={(e) => setCoinForm({ ...coinForm, name: e.target.value })}
              style={inputStyle}
              placeholder="Zogzila Coin"
            />
          </label>
          <label style={{ fontSize: 12, color: '#6b7280' }}>
            Symbol
            <input
              value={coinForm.symbol}
              onChange={(e) => setCoinForm({ ...coinForm, symbol: e.target.value })}
              style={inputStyle}
              placeholder="ZGZ"
            />
          </label>
          <label style={{ fontSize: 12, color: '#6b7280' }}>
            Blockchain
            <select
              value={coinForm.blockchain}
              onChange={(e) => setCoinForm({ ...coinForm, blockchain: e.target.value })}
              style={inputStyle}
            >
              <option value="ethereum">ethereum</option>
              <option value="polygon">polygon</option>
            </select>
          </label>
          <label style={{ fontSize: 12, color: '#6b7280' }}>
            Network
            <select
              value={coinForm.networkType}
              onChange={(e) => setCoinForm({ ...coinForm, networkType: e.target.value })}
              style={inputStyle}
            >
              <option value="TESTNET">TESTNET (recommended)</option>
              <option value="MAINNET">MAINNET</option>
            </select>
          </label>
          <label style={{ fontSize: 12, color: '#6b7280' }}>
            Token standard
            <select
              value={coinForm.tokenStandard}
              onChange={(e) => setCoinForm({ ...coinForm, tokenStandard: e.target.value })}
              style={inputStyle}
            >
              <option value="ERC-20">ERC-20</option>
              <option value="BEP-20">BEP-20</option>
            </select>
          </label>
          <label style={{ fontSize: 12, color: '#6b7280' }}>
            Decimals
            <input
              type="number"
              value={coinForm.decimals}
              onChange={(e) => setCoinForm({ ...coinForm, decimals: Number(e.target.value) })}
              style={inputStyle}
            />
          </label>
          <label style={{ fontSize: 12, color: '#6b7280', gridColumn: '1 / -1' }}>
            Contract address (optional until REGISTERED)
            <input
              value={coinForm.contractAddress}
              onChange={(e) => setCoinForm({ ...coinForm, contractAddress: e.target.value })}
              style={inputStyle}
              placeholder="0x…"
            />
          </label>
          <label style={{ fontSize: 12, color: '#6b7280' }}>
            Total supply (info)
            <input
              value={coinForm.totalSupply}
              onChange={(e) => setCoinForm({ ...coinForm, totalSupply: e.target.value })}
              style={inputStyle}
              placeholder="1000000000"
            />
          </label>
          <label style={{ fontSize: 12, color: '#6b7280', gridColumn: 'span 2' }}>
            Notes
            <input
              value={coinForm.notes}
              onChange={(e) => setCoinForm({ ...coinForm, notes: e.target.value })}
              style={inputStyle}
              placeholder="Deployed on Sepolia…"
            />
          </label>
        </div>
        <button
          className="refresh-btn"
          style={{ marginTop: 12, background: '#086afb', color: '#fff', border: 'none' }}
          onClick={handleCreateCoin}
          disabled={savingCoin}
        >
          {savingCoin ? 'Saving…' : 'Register coin'}
        </button>
      </div>

      {/* Coin list + listing status */}
      <div style={{ background: '#fff', border: '1px solid #e5e7eb', borderRadius: 10, padding: 16, marginBottom: 20 }}>
        <h3 style={{ marginTop: 0, fontSize: 16 }}>Registered coins</h3>
        <div style={{ overflowX: 'auto' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
            <thead>
              <tr style={{ textAlign: 'left', borderBottom: '1px solid #e5e7eb' }}>
                <th style={thStyle}>Symbol</th>
                <th style={thStyle}>Name</th>
                <th style={thStyle}>Chain</th>
                <th style={thStyle}>Network</th>
                <th style={thStyle}>Contract</th>
                <th style={thStyle}>Status</th>
              </tr>
            </thead>
            <tbody>
              {coins.length === 0 && (
                <tr>
                  <td colSpan={6} style={{ padding: 12, color: '#6b7280' }}>No platform coins yet.</td>
                </tr>
              )}
              {coins.map((c) => (
                <tr key={c.id} style={{ borderBottom: '1px solid #f3f4f6' }}>
                  <td style={tdStyle}><strong>{c.symbol}</strong></td>
                  <td style={tdStyle}>{c.name}</td>
                  <td style={tdStyle}>{c.blockchain}</td>
                  <td style={tdStyle}>{statusBadge(c.networkType)}</td>
                  <td style={{ ...tdStyle, fontFamily: 'monospace', fontSize: 11 }}>
                    {c.contractAddress || '—'}
                  </td>
                  <td style={tdStyle}>{statusBadge(c.status)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 10, marginTop: 14, alignItems: 'flex-end' }}>
          <label style={{ fontSize: 12, color: '#6b7280' }}>
            Coin
            <select
              value={listingStatusId ?? ''}
              onChange={(e) => setListingStatusId(e.target.value ? Number(e.target.value) : null)}
              style={inputStyle}
            >
              <option value="">Select…</option>
              {coins.map((c) => (
                <option key={c.id} value={c.id}>{c.symbol} ({c.status})</option>
              ))}
            </select>
          </label>
          <label style={{ fontSize: 12, color: '#6b7280' }}>
            External listing status
            <select
              value={listingStatus}
              onChange={(e) => setListingStatus(e.target.value)}
              style={inputStyle}
            >
              <option value="DRAFT">DRAFT</option>
              <option value="REGISTERED">REGISTERED</option>
              <option value="LISTING_PENDING">LISTING_PENDING</option>
              <option value="LISTED_EXTERNAL">LISTED_EXTERNAL</option>
            </select>
          </label>
          <label style={{ fontSize: 12, color: '#6b7280' }}>
            KuCoin base (e.g. ZGZ)
            <input value={kucoinBase} onChange={(e) => setKucoinBase(e.target.value)} style={inputStyle} placeholder="ZGZ" />
          </label>
          <label style={{ fontSize: 12, color: '#6b7280' }}>
            Spot pair
            <input value={kucoinSpot} onChange={(e) => setKucoinSpot(e.target.value)} style={inputStyle} placeholder="ZGZ-USDT" />
          </label>
          <label style={{ fontSize: 12, color: '#6b7280', display: 'flex', alignItems: 'center', gap: 8, marginBottom: 8 }}>
            <input type="checkbox" checked={tradingEnabled} onChange={(e) => setTradingEnabled(e.target.checked)} />
            Enable in trading universes (requires LISTED_EXTERNAL)
          </label>
          <button className="refresh-btn" onClick={handleUpdateListingStatus} disabled={actionBusy || !listingStatusId}>
            Update status / trading map
          </button>
        </div>
      </div>

      {/* Testnet deploy */}
      <div style={{ background: '#fff', border: '1px solid #e5e7eb', borderRadius: 10, padding: 16, marginBottom: 20 }}>
        <h3 style={{ marginTop: 0, fontSize: 16 }}>Testnet ERC-20 deploy</h3>
        <p style={{ fontSize: 12, color: '#6b7280', marginTop: 0 }}>
          Deploys bundled <code>SimplePlatformERC20</code> on Sepolia/Amoy only. Mainnet auto-deploy is blocked.
          Flag: <strong>{deployConfig?.deployEnabled ? 'ENABLED' : 'DISABLED'}</strong>
          {' '}(<code>platform.coin.deploy-enabled</code>); also needs <code>blockchain.enabled=true</code>.
        </p>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(220px, 1fr))', gap: 10 }}>
          <label style={{ fontSize: 12, color: '#6b7280' }}>
            TESTNET coin without contract
            <select
              value={deployCoinId ?? ''}
              onChange={(e) => setDeployCoinId(e.target.value ? Number(e.target.value) : null)}
              style={inputStyle}
            >
              <option value="">Select…</option>
              {coins
                .filter((c) => c.networkType === 'TESTNET' && !c.contractAddress)
                .map((c) => (
                  <option key={c.id} value={c.id}>{c.symbol} ({c.blockchain})</option>
                ))}
            </select>
          </label>
          <label style={{ fontSize: 12, color: '#6b7280' }}>
            Deployer wallet
            <select value={deployWalletId} onChange={(e) => setDeployWalletId(e.target.value)} style={inputStyle}>
              <option value="">Select…</option>
              {adminWallets.map((w) => (
                <option key={w.id} value={w.id}>#{w.id} {w.blockchain} — {w.address}</option>
              ))}
            </select>
          </label>
        </div>
        <button
          className="refresh-btn"
          style={{
            marginTop: 12,
            background: deployConfig?.deployEnabled ? '#086afb' : '#9ca3af',
            color: '#fff',
            border: 'none',
          }}
          onClick={runTestnetDeploy}
          disabled={actionBusy || !deployConfig?.deployEnabled}
        >
          {deployConfig?.deployEnabled ? 'Deploy on testnet' : 'Deploy disabled'}
        </button>
      </div>

      {/* Campaign create */}
      <div style={{ background: '#fff', border: '1px solid #e5e7eb', borderRadius: 10, padding: 16, marginBottom: 20 }}>
        <h3 style={{ marginTop: 0, fontSize: 16 }}>Create airdrop campaign</h3>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(200px, 1fr))', gap: 10 }}>
          <label style={{ fontSize: 12, color: '#6b7280' }}>
            Platform coin
            <select
              value={campaignForm.platformCoinId}
              onChange={(e) => setCampaignForm({ ...campaignForm, platformCoinId: e.target.value })}
              style={inputStyle}
            >
              <option value="">Select…</option>
              {coins.map((c) => (
                <option key={c.id} value={c.id}>{c.symbol} — {c.blockchain}/{c.networkType}</option>
              ))}
            </select>
          </label>
          <label style={{ fontSize: 12, color: '#6b7280' }}>
            Campaign name
            <input
              value={campaignForm.name}
              onChange={(e) => setCampaignForm({ ...campaignForm, name: e.target.value })}
              style={inputStyle}
              placeholder="Launch airdrop"
            />
          </label>
          <label style={{ fontSize: 12, color: '#6b7280' }}>
            Amount per user
            <input
              value={campaignForm.amountPerUser}
              onChange={(e) => setCampaignForm({ ...campaignForm, amountPerUser: e.target.value })}
              style={inputStyle}
              placeholder="100"
            />
          </label>
          <label style={{ fontSize: 12, color: '#6b7280' }}>
            Eligibility
            <select
              value={campaignForm.eligibility}
              onChange={(e) => setCampaignForm({ ...campaignForm, eligibility: e.target.value })}
              style={inputStyle}
            >
              <option value="ALL_WITH_WALLET">ALL_WITH_WALLET</option>
              <option value="KYC_WITH_WALLET">KYC_WITH_WALLET</option>
            </select>
          </label>
          <label style={{ fontSize: 12, color: '#6b7280' }}>
            Delivery mode
            <select
              value={campaignForm.deliveryMode}
              onChange={(e) => setCampaignForm({ ...campaignForm, deliveryMode: e.target.value })}
              style={inputStyle}
            >
              <option value="PUSH">PUSH (admin batch send)</option>
              <option value="CLAIM">CLAIM (users claim)</option>
            </select>
          </label>
        </div>
        <button
          className="refresh-btn"
          style={{ marginTop: 12, background: '#086afb', color: '#fff', border: 'none' }}
          onClick={handleCreateCampaign}
          disabled={savingCampaign}
        >
          {savingCampaign ? 'Saving…' : 'Create campaign'}
        </button>
      </div>

      {/* Campaigns */}
      <div style={{ background: '#fff', border: '1px solid #e5e7eb', borderRadius: 10, padding: 16, marginBottom: 20 }}>
        <h3 style={{ marginTop: 0, fontSize: 16 }}>Airdrop campaigns</h3>
        <div style={{ overflowX: 'auto' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
            <thead>
              <tr style={{ textAlign: 'left', borderBottom: '1px solid #e5e7eb' }}>
                <th style={thStyle}>Name</th>
                <th style={thStyle}>Coin</th>
                <th style={thStyle}>Mode</th>
                <th style={thStyle}>Amount</th>
                <th style={thStyle}>Status</th>
                <th style={thStyle}>Eligible</th>
                <th style={thStyle}>Sent / Fail</th>
                <th style={thStyle}>Actions</th>
              </tr>
            </thead>
            <tbody>
              {campaigns.length === 0 && (
                <tr>
                  <td colSpan={8} style={{ padding: 12, color: '#6b7280' }}>No campaigns yet.</td>
                </tr>
              )}
              {campaigns.map((c) => (
                <tr key={c.id} style={{ borderBottom: '1px solid #f3f4f6' }}>
                  <td style={tdStyle}>{c.name}</td>
                  <td style={tdStyle}>{c.platformCoinSymbol || c.platformCoinId}</td>
                  <td style={tdStyle}>{c.deliveryMode || 'PUSH'}</td>
                  <td style={tdStyle}>{String(c.amountPerUser)}</td>
                  <td style={tdStyle}>{statusBadge(c.status)}</td>
                  <td style={tdStyle}>{c.eligibleCount ?? c.dryRunOkCount ?? 0}</td>
                  <td style={tdStyle}>{c.sentCount ?? 0} / {c.failedCount ?? 0}</td>
                  <td style={tdStyle}>
                    <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
                      <button className="refresh-btn" disabled={actionBusy} onClick={() => openCampaignDetail(c.id)}>
                        View
                      </button>
                      <button className="refresh-btn" disabled={actionBusy || c.status === 'CANCELLED'} onClick={() => runPreview(c.id)}>
                        Preview
                      </button>
                      <button className="refresh-btn" disabled={actionBusy || c.status === 'CANCELLED'} onClick={() => runDryRun(c.id)}>
                        Dry-run
                      </button>
                      <button
                        className="refresh-btn"
                        disabled={actionBusy || c.status === 'CANCELLED' || c.status === 'EXECUTED'}
                        onClick={() => setExecuteCampaignId(c.id)}
                      >
                        Prepare execute
                      </button>
                      <button className="refresh-btn" disabled={actionBusy || c.status === 'CANCELLED'} onClick={() => cancelCampaign(c.id)}>
                        Cancel
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* Phase 2 execute */}
      <div style={{ background: '#fff', border: '1px solid #e5e7eb', borderRadius: 10, padding: 16, marginBottom: 20 }}>
        <h3 style={{ marginTop: 0, fontSize: 16 }}>Execute / arm claims (Phase 2)</h3>
        <p style={{ fontSize: 12, color: '#6b7280', marginTop: 0 }}>
          Requires dry-run first. Funding wallet must match the coin chain. Create wallets in Crypto Payouts if needed.
        </p>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(220px, 1fr))', gap: 10 }}>
          <label style={{ fontSize: 12, color: '#6b7280' }}>
            Campaign
            <select
              value={executeCampaignId ?? ''}
              onChange={(e) => setExecuteCampaignId(e.target.value ? Number(e.target.value) : null)}
              style={inputStyle}
            >
              <option value="">Select…</option>
              {campaigns.map((c) => (
                <option key={c.id} value={c.id}>
                  {c.name} ({c.deliveryMode || 'PUSH'} / {c.status})
                </option>
              ))}
            </select>
          </label>
          <label style={{ fontSize: 12, color: '#6b7280' }}>
            Funding wallet
            <select
              value={fundingWalletId}
              onChange={(e) => setFundingWalletId(e.target.value)}
              style={inputStyle}
            >
              <option value="">Select…</option>
              {adminWallets.map((w) => (
                <option key={w.id} value={w.id}>
                  #{w.id} {w.blockchain} — {w.address}
                </option>
              ))}
            </select>
          </label>
        </div>
        <button
          className="refresh-btn"
          style={{
            marginTop: 12,
            background: executeConfig?.executeEnabled ? '#e7038e' : '#9ca3af',
            color: '#fff',
            border: 'none',
          }}
          onClick={runExecute}
          disabled={actionBusy || !executeConfig?.executeEnabled}
        >
          {executeConfig?.executeEnabled
            ? (campaigns.find((c) => c.id === executeCampaignId)?.deliveryMode === 'CLAIM'
              ? 'Open claims'
              : 'Execute live PUSH send')
            : 'Execute disabled (set platform.airdrop.execute-enabled=true)'}
        </button>
      </div>

      {/* Recipient detail */}
      {selectedCampaign && (
        <div style={{ background: '#fff', border: '1px solid #e5e7eb', borderRadius: 10, padding: 16 }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 12 }}>
            <h3 style={{ margin: 0, fontSize: 16 }}>
              Recipients — {selectedCampaign.name} {statusBadge(selectedCampaign.status)}
            </h3>
            <button className="refresh-btn" onClick={() => setSelectedCampaign(null)}>Close</button>
          </div>
          <p style={{ fontSize: 12, color: '#6b7280' }}>
            Eligible: {selectedCampaign.eligibleCount ?? 0} · Missing wallet: {selectedCampaign.missingWalletCount ?? 0} ·
            Invalid: {selectedCampaign.invalidAddressCount ?? 0} · Dry-run OK: {selectedCampaign.dryRunOkCount ?? 0} ·
            Sent: {selectedCampaign.sentCount ?? 0} · Failed: {selectedCampaign.failedCount ?? 0} ·
            Claimable: {selectedCampaign.claimableCount ?? 0}
          </p>
          <div style={{ overflowX: 'auto', maxHeight: 420 }}>
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12 }}>
              <thead>
                <tr style={{ textAlign: 'left', borderBottom: '1px solid #e5e7eb' }}>
                  <th style={thStyle}>User</th>
                  <th style={thStyle}>Wallet</th>
                  <th style={thStyle}>Amount</th>
                  <th style={thStyle}>Status</th>
                  <th style={thStyle}>Tx</th>
                  <th style={thStyle}>Message</th>
                </tr>
              </thead>
              <tbody>
                {(selectedCampaign.recipients || []).length === 0 && (
                  <tr>
                    <td colSpan={6} style={{ padding: 12, color: '#6b7280' }}>
                      No recipients yet — run Preview.
                    </td>
                  </tr>
                )}
                {(selectedCampaign.recipients || []).map((r) => (
                  <tr key={r.id} style={{ borderBottom: '1px solid #f3f4f6' }}>
                    <td style={tdStyle}>{r.username}</td>
                    <td style={{ ...tdStyle, fontFamily: 'monospace', fontSize: 11 }}>{r.walletAddress || '—'}</td>
                    <td style={tdStyle}>{String(r.amount)}</td>
                    <td style={tdStyle}>{statusBadge(r.status)}</td>
                    <td style={{ ...tdStyle, fontFamily: 'monospace', fontSize: 10 }}>{r.txHash || '—'}</td>
                    <td style={tdStyle}>{r.errorMessage || r.validationMessage || '—'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
};

const inputStyle: React.CSSProperties = {
  display: 'block',
  width: '100%',
  marginTop: 4,
  padding: '8px 10px',
  borderRadius: 8,
  border: '1px solid #d1d5db',
  fontSize: 13,
  boxSizing: 'border-box',
};

const thStyle: React.CSSProperties = { padding: '8px 6px', color: '#6b7280', fontWeight: 600 };
const tdStyle: React.CSSProperties = { padding: '8px 6px', verticalAlign: 'top' };

export default AdminPlatformCoins;
